import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GtmSolution } from '../../types/go-to-market';
import { fetchItems } from '../queries';
import { RootState } from '../store';


/* Slice for Go-To-Market Solutions data, specifically focused on Architecture type for now.
 * GTM Solutions are read only outside of admin tasks, and primarily used for documentation.
*/

export const fetchGtmSolutions = createAsyncThunk(
  'gtmSolutions/fetchSolutions',
  async () => {
    const data = await fetchItems('db/go-to-market', { type: 'architecture', detailed: true });
    return data as GtmSolution[];
  }
);

export const checkGtmSolutionsLoaded = () => async (dispatch, getState) => {
  const state = getState() as RootState;
  if (state.gtmSolutions.architectures?.length === 0 && !state.gtmSolutions.isInitialized && !state.gtmSolutions.isLoading) {
    await dispatch(fetchGtmSolutions());
  }
};

interface GtmSolutionsState {
  architectures: GtmSolution[];
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

const initialState: GtmSolutionsState = {
  architectures: [],
  isLoading: false,
  isInitialized: false,
  error: null,
};

const gtmSolutionsSlice = createSlice({
  name: 'gtmSolutions',
  initialState,
  reducers: {
    addGtmArchitecture(state, action: PayloadAction<GtmSolution>) {
      state.architectures.push(action.payload);
    },
    updateGtmArchitecture(state, action: PayloadAction<GtmSolution>) {
      const idx = state.architectures.findIndex(p => p.id === action.payload.id);
      if (idx !== -1) {
        state.architectures[idx] = action.payload;
      }
    },
    deleteGtmArchitecture(state, action: PayloadAction<GtmSolution['id']>) {
      state.architectures = state.architectures.filter(p => p.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGtmSolutions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchGtmSolutions.fulfilled, (state, action: PayloadAction<GtmSolution[]>) => {
        state.architectures = action.payload;
        state.isLoading = false;
        state.isInitialized = true;
        state.error = null;
      })
      .addCase(fetchGtmSolutions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load GTM Solutions';
      });
  }
});

export const {
  addGtmArchitecture,
  updateGtmArchitecture,
  deleteGtmArchitecture,
} = gtmSolutionsSlice.actions;
export default gtmSolutionsSlice.reducer;